export const WILL_CALL_DESTINATION = 'will_call';

export const BUILT_IN_FREIGHT_DESTINATIONS = [
  { value: 'designer_warehouse', label: 'To Designer Warehouse' },
  { value: 'client_site', label: 'Direct to Client Site' },
  { value: WILL_CALL_DESTINATION, label: 'Will Call' },
];

// A saved destination may carry the receiver/workroom address it ships to, so
// picking it on a purchase order fills Ship To instead of the user retyping it.
export const DESTINATION_ADDRESS_FIELDS = [
  'company',
  'street',
  'city',
  'state',
  'zip',
  'phone',
  'email',
];

const str = (v) => (typeof v === 'string' ? v.trim() : '');

export function getDestinationAddress(destination) {
  const address = {};
  for (const field of DESTINATION_ADDRESS_FIELDS) {
    address[field] = str(destination?.[field]);
  }
  return address;
}

export function hasDestinationAddress(destination) {
  const address = getDestinationAddress(destination);
  return DESTINATION_ADDRESS_FIELDS.some((field) => address[field]);
}

// Merges the built-in destinations with the account's saved ones. A saved entry
// whose value matches a built-in is an *override* — it keeps the built-in label
// and position but contributes its saved address, which is how the firm's own
// warehouse address gets attached to "To Designer Warehouse".
export function getFreightDestinationOptions(customDestinations) {
  const saved = Array.isArray(customDestinations) ? customDestinations : [];
  const savedByValue = new Map(saved.filter((d) => d?.value).map((d) => [d.value, d]));

  const builtIns = BUILT_IN_FREIGHT_DESTINATIONS.map((builtIn) => ({
    ...getDestinationAddress(savedByValue.get(builtIn.value)),
    ...builtIn,
  }));

  const builtInValues = new Set(BUILT_IN_FREIGHT_DESTINATIONS.map((d) => d.value));
  const extra = saved
    .filter((d) => d?.value && !builtInValues.has(d.value))
    .map((d) => ({ ...getDestinationAddress(d), value: d.value, label: d.label || d.value }));

  return [...builtIns, ...extra];
}

export function getFreightDestinationLabel(value, customDestinations) {
  const options = getFreightDestinationOptions(customDestinations);
  return options.find((o) => o.value === value)?.label || value || BUILT_IN_FREIGHT_DESTINATIONS[0].label;
}

// Returns the saved address for a destination, or null when it has none — the
// caller then falls back to company settings / the project's own addresses.
export function getFreightDestinationAddress(value, customDestinations) {
  const match = getFreightDestinationOptions(customDestinations).find((o) => o.value === value);
  if (!match || !hasDestinationAddress(match)) return null;
  return getDestinationAddress(match);
}

// "Will Call" means the vendor holds the goods for pickup, so a PO set to it
// must not carry a Ship To name/address at all. Matches the built-in value and
// a one-off per-item destination someone typed as "Will Call" by hand.
export function isWillCallDestination(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return normalized === WILL_CALL_DESTINATION;
}

// Generates a stable, unique slug for a new custom destination from its label.
export function slugifyFreightDestination(label, existingValues) {
  const taken = new Set(existingValues || []);
  const base = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'destination';

  let candidate = base;
  let n = 2;
  while (taken.has(candidate)) {
    candidate = `${base}_${n}`;
    n += 1;
  }
  return candidate;
}
